import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  GridLegacy as Grid,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { format, parseISO } from 'date-fns';

// Types
interface User {
  id: number;
  username: string;
  email: string;
  sales_tracker_id?: string;
}

interface Spiff {
  id: number;
  user_id: number;
  username: string;
  sales_tracker_id: string;
  description: string;
  amount: number;
  created_at: string;
  created_by: number;
  creator_username: string;
  spiff_date?: string;
}

interface CreateSpiffData {
  user_id: number;
  description: string;
  amount: number;
  spiff_date?: string;
}

interface UpdateSpiffData {
  description: string;
  amount: number;
  spiff_date?: string;
}

const SpiffManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSpiff, setEditingSpiff] = useState<Spiff | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [spiffDate, setSpiffDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch all SPIFFs
  const { data: spiffs, isLoading: loadingSpiffs, refetch } = useQuery<Spiff[]>({
    queryKey: ['spiffs'],
    queryFn: async () => {
      return await api.getAllSpiffs();
    },
  });

  // Fetch active users
  const { data: users, isLoading: loadingUsers } = useQuery<User[]>({
    queryKey: ['activeUsers'],
    queryFn: async () => {
      return await api.getActiveUsersForSpiff();
    },
  });

  // Create SPIFF mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateSpiffData) => {
      return await api.createSpiff(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spiffs'] });
      setSuccess('SPIFF added successfully!');
      handleCloseDialog();
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to add SPIFF');
      setTimeout(() => setError(null), 5000);
    },
  });

  // Update SPIFF mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateSpiffData }) => {
      return await api.updateSpiff(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spiffs'] });
      setSuccess('SPIFF updated successfully!');
      handleCloseDialog();
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to update SPIFF');
      setTimeout(() => setError(null), 5000);
    },
  });

  // Delete SPIFF mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await api.deleteSpiff(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spiffs'] });
      setSuccess('SPIFF deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to delete SPIFF');
      setTimeout(() => setError(null), 5000);
    },
  });

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setEditingSpiff(null);
    setSelectedUserId(0);
    setDescription('');
    setAmount('');
    setSpiffDate(format(new Date(), 'yyyy-MM-dd'));
    setError(null);
  };

  const handleOpenEditDialog = (spiff: Spiff) => {
    setOpenDialog(true);
    setEditingSpiff(spiff);
    setSelectedUserId(spiff.user_id);
    setDescription(spiff.description);
    setAmount(spiff.amount.toString());
    setSpiffDate(spiff.spiff_date || format(parseISO(spiff.created_at), 'yyyy-MM-dd'));
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSpiff(null);
    setSelectedUserId(0);
    setDescription('');
    setAmount('');
    setSpiffDate(format(new Date(), 'yyyy-MM-dd'));
    setError(null);
  };

  const handleSubmit = () => {
    if (editingSpiff) {
      // Update existing SPIFF
      if (!description.trim()) {
        setError('Please enter a description');
        return;
      }
      if (!amount || parseFloat(amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      updateMutation.mutate({
        id: editingSpiff.id,
        data: {
          description: description.trim(),
          amount: parseFloat(amount),
          spiff_date: spiffDate,
        },
      });
    } else {
      // Create new SPIFF
      if (!selectedUserId) {
        setError('Please select a user');
        return;
      }
      if (!description.trim()) {
        setError('Please enter a description');
        return;
      }
      if (!amount || parseFloat(amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      createMutation.mutate({
        user_id: selectedUserId,
        description: description.trim(),
        amount: parseFloat(amount),
        spiff_date: spiffDate,
      });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this SPIFF?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleUserChange = (event: SelectChangeEvent<number>) => {
    setSelectedUserId(event.target.value as number);
  };

  // Calculate totals
  const totalSpiffs = spiffs?.length || 0;
  const totalAmount = spiffs?.reduce((sum, spiff) => sum + spiff.amount, 0) || 0;
  const uniqueUsers = new Set(spiffs?.map(s => s.user_id)).size;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrophyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            SPIFF Manager
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={loadingSpiffs}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add SPIFF
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrophyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total SPIFFs
                  </Typography>
                  <Typography variant="h4">
                    {totalSpiffs}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Amount
                  </Typography>
                  <Typography variant="h4">
                    ${totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Recipients
                  </Typography>
                  <Typography variant="h4">
                    {uniqueUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SPIFFs Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Sales Tracker ID</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell><strong>SPIFF Date</strong></TableCell>
                <TableCell><strong>Created By</strong></TableCell>
                <TableCell><strong>Created Date</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingSpiffs ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : spiffs && spiffs.length > 0 ? (
                spiffs.map((spiff) => (
                  <TableRow key={spiff.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {spiff.sales_tracker_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {spiff.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{spiff.description}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        ${spiff.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {spiff.spiff_date ? format(parseISO(spiff.spiff_date), 'MMM dd, yyyy') : format(parseISO(spiff.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>{spiff.creator_username}</TableCell>
                    <TableCell>
                      {format(parseISO(spiff.created_at), 'MMM dd, yyyy h:mm a')}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(spiff)}
                          disabled={updateMutation.isPending}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(spiff.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No SPIFFs found. Click "Add SPIFF" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit SPIFF Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon color="primary" />
            {editingSpiff ? 'Edit SPIFF' : 'Add New SPIFF'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* User Selection - Only show for new SPIFFs */}
            {!editingSpiff && (
              <FormControl fullWidth>
                <InputLabel>Select User *</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={handleUserChange}
                  label="Select User *"
                >
                  <MenuItem value={0}>
                    <em>Select a user...</em>
                  </MenuItem>
                  {loadingUsers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading users...
                    </MenuItem>
                  ) : (
                    users?.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.username} - {user.sales_tracker_id || 'No Sales Tracker ID'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Date Picker */}
            <TextField
              fullWidth
              label="SPIFF Date *"
              type="date"
              value={spiffDate}
              onChange={(e) => setSpiffDate(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* Description */}
            <TextField
              fullWidth
              label="SPIFF Description *"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              placeholder="e.g., Q1 Top Performer Bonus, Customer Satisfaction Award, etc."
            />

            {/* Amount */}
            <TextField
              fullWidth
              label="Amount *"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: 0.01,
              }}
              placeholder="0.00"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={createMutation.isPending || updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
            startIcon={(createMutation.isPending || updateMutation.isPending) ? <CircularProgress size={20} /> : editingSpiff ? <EditIcon /> : <AddIcon />}
          >
            {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : editingSpiff ? 'Update SPIFF' : 'Add SPIFF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SpiffManager;
